import { useMemo, useState, useEffect } from 'react';
import { ArrowRight, Plus, Calendar, TrendingUp, Briefcase, Zap } from 'lucide-react';

// ============================================================================
// Streak Calculator - Consecutive days with activity
// ============================================================================
function calculateStreak(applications) {
  if (!applications.length) return 0;

  // Get unique activity dates (tailoring or applying)
  const activityDates = new Set();
  applications.forEach(app => {
    const tailorDate = new Date(app.timestamp).toDateString();
    activityDates.add(tailorDate);
    if (app.appliedDate) {
      const applyDate = new Date(app.appliedDate).toDateString();
      activityDates.add(applyDate);
    }
  });

  // Convert to sorted array of dates
  const sortedDates = Array.from(activityDates)
    .map(d => new Date(d))
    .sort((a, b) => b - a); // Most recent first

  if (sortedDates.length === 0) return 0;

  // Check if most recent activity was today or yesterday
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const mostRecent = new Date(sortedDates[0]);
  mostRecent.setHours(0, 0, 0, 0);

  // If no activity today or yesterday, streak is 0
  if (mostRecent < yesterday) return 0;

  // Count consecutive days backwards
  let streak = 1;
  let currentDate = mostRecent;

  for (let i = 1; i < sortedDates.length; i++) {
    const prevDate = new Date(sortedDates[i]);
    prevDate.setHours(0, 0, 0, 0);

    const expectedPrev = new Date(currentDate);
    expectedPrev.setDate(expectedPrev.getDate() - 1);

    if (prevDate.getTime() === expectedPrev.getTime()) {
      streak++;
      currentDate = prevDate;
    } else if (prevDate.getTime() === currentDate.getTime()) {
      // Same day, skip
      continue;
    } else {
      // Gap in streak
      break;
    }
  }

  return streak;
}

// ============================================================================
// Streak Display - Simple, human
// ============================================================================
function StreakDisplay({ streak, hasActivityToday }) {
  if (streak === 0) return null;

  return (
    <div className="flex items-center gap-2">
      <Zap size={16} className="text-slate-400" />
      <span className="text-sm text-slate-600">
        <span className="font-semibold text-slate-900">{streak}</span> day{streak !== 1 ? 's' : ''} in a row
      </span>
      {!hasActivityToday && streak > 0 && (
        <span className="text-xs text-slate-400">· keep it going</span>
      )}
    </div>
  );
}

// ============================================================================
// Weekly Goal - Progress ring with clean copy
// ============================================================================
function WeeklyGoal({ current, goal, onSetGoal }) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempGoal, setTempGoal] = useState(goal);

  const percentage = Math.min((current / goal) * 100, 100);
  const remaining = Math.max(goal - current, 0);
  const isComplete = current >= goal;

  // SVG progress ring
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const handleSave = () => {
    const newGoal = Math.max(1, Math.min(50, parseInt(tempGoal) || 5));
    onSetGoal(newGoal);
    setIsEditing(false);
  };

  return (
    <div className="flex items-center gap-4">
      {/* Progress Ring */}
      <div className="relative w-12 h-12 flex-shrink-0">
        <svg className="w-12 h-12 transform -rotate-90">
          {/* Background circle */}
          <circle
            cx="24"
            cy="24"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
            fill="none"
            className="text-slate-100"
          />
          {/* Progress circle */}
          <circle
            cx="24"
            cy="24"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            className={isComplete ? "text-green-500" : "text-primary"}
            style={{
              strokeDasharray: circumference,
              strokeDashoffset,
              transition: 'stroke-dashoffset 0.5s ease'
            }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-sm font-semibold ${isComplete ? 'text-green-600' : 'text-slate-700'}`}>
            {current}
          </span>
        </div>
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              max="50"
              value={tempGoal}
              onChange={(e) => setTempGoal(e.target.value)}
              className="w-16 px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            />
            <span className="text-sm text-slate-500">per week</span>
            <button
              onClick={handleSave}
              className="text-sm text-primary hover:text-dark font-medium cursor-pointer"
            >
              Save
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-700">
              {isComplete ? (
                <span className="font-medium text-green-600">Goal reached</span>
              ) : (
                <>
                  <span className="font-semibold text-slate-900">{remaining}</span> more to reach your goal
                </>
              )}
            </p>
            <button
              onClick={() => setIsEditing(true)}
              className="text-xs text-slate-400 hover:text-slate-600 cursor-pointer"
            >
              {current} of {goal} this week · Edit goal
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Interview helpers
// ============================================================================
const ROUND_TYPE_LABELS = {
  phone_screen: 'Phone Screen',
  recruiter: 'Recruiter Call',
  technical: 'Technical',
  behavioral: 'Behavioral',
  onsite: 'Onsite',
  final: 'Final Round',
  other: 'Interview',
};

function formatInterviewDate(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays > 1 && diffDays <= 7) return `In ${diffDays} days`;

  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// ============================================================================
// UpcomingInterviews
// ============================================================================
function UpcomingInterviews({ interviewData, loading, onNavigate }) {
  const applicationInterviews = useMemo(() => {
    const grouped = {};
    interviewData.forEach(interview => {
      if (!grouped[interview.application_id]) {
        grouped[interview.application_id] = {
          applicationId: interview.application_id,
          company: interview.company,
          role: interview.role,
          applicationStatus: interview.application_status,
          rounds: [],
        };
      }
      grouped[interview.application_id].rounds.push(interview);
    });

    const interviewApps = Object.values(grouped).filter(
      app => app.applicationStatus === 'interviewing'
    );

    interviewApps.forEach(app => {
      app.rounds.sort((a, b) =>
        new Date(a.scheduled_date || '9999') - new Date(b.scheduled_date || '9999')
      );
      app.nextInterview = app.rounds.find(
        r => r.round_status === 'scheduled' && r.scheduled_date && new Date(r.scheduled_date) >= new Date(new Date().setHours(0,0,0,0))
      );
    });

    return interviewApps
      .filter(app => app.nextInterview)
      .sort((a, b) => new Date(a.nextInterview.scheduled_date) - new Date(b.nextInterview.scheduled_date))
      .slice(0, 3);
  }, [interviewData]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="h-4 bg-slate-100 rounded w-32 animate-pulse mb-4"></div>
        <div className="space-y-3">
          <div className="h-14 bg-slate-50 rounded-lg animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (applicationInterviews.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-medium text-slate-900 flex items-center gap-2">
          <Calendar size={16} className="text-primary" />
          Upcoming Interviews
        </h2>
        <button
          onClick={() => onNavigate('library', 'interview')}
          className="text-sm text-primary hover:text-primary/80 transition-colors cursor-pointer"
        >
          View all
        </button>
      </div>

      <div className="space-y-2">
        {applicationInterviews.map((app) => (
          <button
            key={app.applicationId}
            onClick={() => onNavigate('library', 'interview')}
            className="w-full flex items-center gap-4 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer text-left group"
          >
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Briefcase size={18} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-900 truncate">{app.company}</p>
              <p className="text-sm text-slate-500">
                {ROUND_TYPE_LABELS[app.nextInterview.round_type]}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-sm font-medium text-primary">
                {formatInterviewDate(app.nextInterview.scheduled_date)}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Stat Card Component - Enhanced with color accents
// ============================================================================
function StatCard({ value, label, color = 'slate', onClick }) {
  const colorStyles = {
    slate: { bg: 'bg-white', dot: 'bg-slate-400', text: 'text-slate-900', hover: 'hover:border-slate-300' },
    cyan: { bg: 'bg-white', dot: 'bg-cyan-500', text: 'text-slate-900', hover: 'hover:border-cyan-200' },
    purple: { bg: 'bg-white', dot: 'bg-primary', text: 'text-primary', hover: 'hover:border-primary/30' },
    green: { bg: 'bg-white', dot: 'bg-green-500', text: 'text-green-600', hover: 'hover:border-green-200' },
  };

  const style = colorStyles[color] || colorStyles.slate;

  const content = (
    <>
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-2 h-2 rounded-full ${style.dot}`}></span>
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
      </div>
      <span className={`text-3xl font-bold ${style.text}`}>
        {value}
      </span>
    </>
  );

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className={`${style.bg} rounded-xl border border-slate-200 p-4 text-left ${style.hover} hover:shadow-sm transition-all cursor-pointer`}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={`${style.bg} rounded-xl border border-slate-200 p-4`}>
      {content}
    </div>
  );
}

// ============================================================================
// Mini Pipeline Visual - Shows the job hunt funnel
// ============================================================================
function PipelineVisual({ stats }) {
  const stages = [
    { label: 'Applied', count: stats.applied, color: 'bg-cyan-500' },
    { label: 'Interview', count: stats.interviews, color: 'bg-primary' },
    { label: 'Offer', count: stats.offers, color: 'bg-green-500' },
  ];

  const maxCount = Math.max(...stages.map(s => s.count), 1);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="text-sm font-medium text-slate-900 mb-4">Your Pipeline</h3>
      <div className="space-y-3">
        {stages.map((stage) => (
          <div key={stage.label} className="flex items-center gap-3">
            <span className="text-xs text-slate-500 w-16">{stage.label}</span>
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full ${stage.color} rounded-full transition-all duration-500`}
                style={{ width: `${Math.max((stage.count / maxCount) * 100, stage.count > 0 ? 8 : 0)}%` }}
              />
            </div>
            <span className="text-sm font-medium text-slate-700 w-8 text-right">{stage.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// DashboardView
// ============================================================================

function DashboardView({ resumeLibrary, authToken, onNavigate, onViewApplication }) {
  const [interviewData, setInterviewData] = useState([]);
  const [loadingInterviews, setLoadingInterviews] = useState(false);

  // Weekly goal - stored in localStorage
  const [weeklyGoal, setWeeklyGoal] = useState(() => {
    const saved = localStorage.getItem('resmaker_weekly_goal');
    return saved ? parseInt(saved) : 5; // Default: 5 per week
  });

  const handleSetWeeklyGoal = (newGoal) => {
    setWeeklyGoal(newGoal);
    localStorage.setItem('resmaker_weekly_goal', newGoal.toString());
  };

  // Calculate streak
  const streak = useMemo(() => calculateStreak(resumeLibrary), [resumeLibrary]);

  // Check if user has activity today
  const hasActivityToday = useMemo(() => {
    const today = new Date().toDateString();
    return resumeLibrary.some(app => {
      const tailorDate = new Date(app.timestamp).toDateString();
      const applyDate = app.appliedDate ? new Date(app.appliedDate).toDateString() : null;
      return tailorDate === today || applyDate === today;
    });
  }, [resumeLibrary]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = resumeLibrary.length;
    const draft = resumeLibrary.filter(app => app.status === 'draft').length;
    const applied = resumeLibrary.filter(app => app.status === 'applied').length;
    const interviews = resumeLibrary.filter(app => app.status === 'interview' || app.status === 'interviewing').length;
    const offers = resumeLibrary.filter(app => app.status === 'offer').length;
    const rejected = resumeLibrary.filter(app => app.status === 'rejected').length;

    const submittedApps = applied + interviews + offers + rejected;
    const interviewRate = submittedApps > 0
      ? Math.round((interviews + offers) / submittedApps * 100)
      : 0;

    // Funnel: count apps that ever reached each stage (even if later rejected)
    const everInterviewed = resumeLibrary.filter(app =>
      (app.statusHistory || []).some(h =>
        h.toStatus === 'interview' || h.toStatus === 'interviewing'
      )
    ).length;
    const everOffered = resumeLibrary.filter(app =>
      (app.statusHistory || []).some(h => h.toStatus === 'offer')
    ).length;
    const funnelInterviewRate = submittedApps > 0
      ? Math.round(everInterviewed / submittedApps * 100)
      : 0;
    const funnelOfferRate = everInterviewed > 0
      ? Math.round(everOffered / everInterviewed * 100)
      : 0;
    const hasHistoryData = resumeLibrary.some(app => (app.statusHistory || []).length > 0);

    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const thisWeek = resumeLibrary.filter(app =>
      new Date(app.timestamp) >= oneWeekAgo
    ).length;

    const isPowerUser = total >= 20;

    return {
      total, draft, applied, interviews, offers, rejected, interviewRate, thisWeek, isPowerUser,
      everInterviewed, everOffered, funnelInterviewRate, funnelOfferRate, hasHistoryData
    };
  }, [resumeLibrary]);

  // Fetch interview data
  useEffect(() => {
    if (authToken && stats.interviews > 0) {
      setLoadingInterviews(true);
      fetch('/api/user/interviews', {
        headers: { 'Authorization': `Bearer ${authToken}` }
      })
        .then(res => res.ok ? res.json() : { interviews: [] })
        .then(data => setInterviewData(data.interviews || []))
        .catch(() => setInterviewData([]))
        .finally(() => setLoadingInterviews(false));
    }
  }, [authToken, stats.interviews]);

  // Get recent applications
  const recentApplications = useMemo(() => {
    return [...resumeLibrary]
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 5);
  }, [resumeLibrary]);

  // Get applications needing follow-up (applied > 7 days ago)
  const needsFollowUp = useMemo(() => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return resumeLibrary.filter(app =>
      app.status === 'applied' &&
      new Date(app.appliedDate || app.timestamp) < sevenDaysAgo
    ).length;
  }, [resumeLibrary]);

  // Get applications needing interview prep (only if they have an interview scheduled)
  const needsPrep = useMemo(() => {
    return resumeLibrary.filter(app =>
      ['interview', 'interviewing'].includes(app.status) &&
      !app.interviewPrep
    ).length;
  }, [resumeLibrary]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const statusColors = {
    draft: 'bg-amber-400',
    applied: 'bg-secondary',
    interview: 'bg-primary',
    interviewing: 'bg-primary',
    offer: 'bg-green-500',
    rejected: 'bg-slate-400',
  };

  const isNewUser = stats.total === 0;

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 sm:px-6">

      {/* ===== NEW USER WELCOME ===== */}
      {isNewUser && (
        <div className="space-y-8">
          {/* Hero Card - Warm, encouraging */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8 sm:p-10">
            <div className="max-w-lg">
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Your next role starts here
              </h1>
              <p className="text-lg text-slate-600 mb-3">
                Every job is different. Your resume should be too.
              </p>
              <p className="text-slate-500 mb-8">
                Paste any job posting and we'll help you highlight the experience that matters most. No fluff, no fake content, just your real story told the right way.
              </p>

              <button
                onClick={() => onNavigate('tailor')}
                className="btn-primary btn-with-icon"
              >
                <Plus size={20} />
                Get started
              </button>
            </div>
          </div>

          {/* How it works - Simple 3 steps */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-4">How it works</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary flex-shrink-0">1</div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Paste the job description</p>
                  <p className="text-xs text-slate-500">We analyze what they're looking for</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary flex-shrink-0">2</div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Get an optimized resume</p>
                  <p className="text-xs text-slate-500">Tailored to match the role</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary flex-shrink-0">3</div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Apply with confidence</p>
                  <p className="text-xs text-slate-500">Track everything in one place</p>
                </div>
              </div>
            </div>
          </div>

          {/* Browse jobs link */}
          <div className="text-center">
            <button
              onClick={() => onNavigate('jobs')}
              className="text-primary hover:text-primary/80 font-medium transition-colors cursor-pointer"
            >
              Or browse job listings to get started
            </button>
          </div>
        </div>
      )}

      {/* ===== RETURNING USER DASHBOARD ===== */}
      {!isNewUser && (
        <div className="space-y-6">

          {/* Welcome back + CTA */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">
                {stats.interviews > 0 ? "You've got momentum" : "Keep it going"}
              </h1>
              {/* Streak inline with subtitle */}
              <div className="flex items-center gap-3 mt-1">
                {streak > 0 && (
                  <StreakDisplay streak={streak} hasActivityToday={hasActivityToday} />
                )}
                {streak === 0 && (
                  <p className="text-slate-500">
                    {stats.thisWeek > 0
                      ? `${stats.thisWeek} application${stats.thisWeek !== 1 ? 's' : ''} this week`
                      : stats.interviews > 0
                        ? `${stats.interviews} active interview${stats.interviews !== 1 ? 's' : ''}`
                        : `${stats.total} application${stats.total !== 1 ? 's' : ''} tracked`
                    }
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => onNavigate('tailor')}
              className="btn-primary btn-with-icon btn-sm"
            >
              <Plus size={18} />
              New application
            </button>
          </div>

          {/* Weekly Goal Card */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <WeeklyGoal
              current={stats.thisWeek}
              goal={weeklyGoal}
              onSetGoal={handleSetWeeklyGoal}
            />
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard
              value={stats.total}
              label="Total"
              color="slate"
              onClick={() => onNavigate('library')}
            />
            <StatCard
              value={stats.applied}
              label="Applied"
              color="cyan"
              onClick={() => onNavigate('library', 'applied')}
            />
            <StatCard
              value={stats.interviews}
              label="Interview"
              color={stats.interviews > 0 ? 'purple' : 'slate'}
              onClick={() => onNavigate('library', 'interview')}
            />
            <StatCard
              value={stats.offers}
              label={stats.offers === 1 ? 'Offer' : 'Offers'}
              color={stats.offers > 0 ? 'green' : 'slate'}
              onClick={() => onNavigate('library', 'offer')}
            />
          </div>

          {/* Pipeline Visual - shows when user has activity */}
          {(stats.applied > 0 || stats.interviews > 0 || stats.offers > 0) && (
            <PipelineVisual stats={stats} />
          )}

          {/* Funnel metrics - shown when history data exists */}
          {stats.hasHistoryData && stats.everInterviewed > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">Your funnel</p>
              <div className="space-y-2">
                {/* Applied bar */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-600">Applied</span>
                    <span className="text-xs font-medium text-slate-700">{stats.applied + stats.interviews + stats.offers + stats.rejected}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full">
                    <div className="h-1.5 bg-secondary rounded-full w-full" />
                  </div>
                </div>
                {/* Interviewed bar */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-600">Reached interview</span>
                    <span className="text-xs font-medium text-slate-700">
                      {stats.everInterviewed}
                      <span className="text-slate-400 font-normal ml-1">({stats.funnelInterviewRate}%)</span>
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full">
                    <div
                      className="h-1.5 bg-primary rounded-full transition-all"
                      style={{ width: `${stats.funnelInterviewRate}%` }}
                    />
                  </div>
                </div>
                {/* Offer bar */}
                {stats.everOffered > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-slate-600">Reached offer</span>
                      <span className="text-xs font-medium text-slate-700">
                        {stats.everOffered}
                        <span className="text-slate-400 font-normal ml-1">({stats.funnelOfferRate}%)</span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full">
                      <div
                        className="h-1.5 bg-green-500 rounded-full transition-all"
                        style={{ width: `${stats.funnelOfferRate}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fallback interview rate for power users without history data */}
          {!stats.hasHistoryData && stats.isPowerUser && stats.interviewRate > 0 && (
            <div className="flex items-center gap-2 px-4 py-3 bg-primary/5 rounded-xl border border-primary/10">
              <TrendingUp size={16} className="text-primary" />
              <span className="text-sm text-slate-600">
                <span className="font-semibold text-primary">{stats.interviewRate}%</span> of your applications led to interviews
              </span>
            </div>
          )}

          {/* Action Cards */}
          <div className="space-y-3">
            {/* Drafts */}
            {stats.draft > 0 && (
              <button
                onClick={() => onNavigate('library', 'draft')}
                className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                  <span className="text-slate-700">
                    {stats.draft} unfinished {stats.draft === 1 ? 'draft' : 'drafts'}
                  </span>
                </div>
                <span className="text-primary font-medium flex items-center gap-1">
                  Continue <ArrowRight size={16} />
                </span>
              </button>
            )}

            {/* Follow-up */}
            {needsFollowUp > 0 && (
              <button
                onClick={() => onNavigate('library', 'applied')}
                className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-secondary"></div>
                  <span className="text-slate-700">
                    {needsFollowUp} {needsFollowUp === 1 ? 'application needs' : 'applications need'} follow-up
                  </span>
                </div>
                <span className="text-slate-500 font-medium flex items-center gap-1">
                  Review <ArrowRight size={16} />
                </span>
              </button>
            )}

            {/* Needs interview prep */}
            {needsPrep > 0 && (
              <button
                onClick={() => onNavigate('library', 'applied')}
                className="w-full flex items-center justify-between p-4 bg-primary/5 rounded-xl border border-primary/10 hover:border-primary/20 hover:shadow-sm transition-all cursor-pointer text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                  <span className="text-slate-700">
                    {needsPrep} {needsPrep === 1 ? 'application' : 'applications'} without interview prep
                  </span>
                </div>
                <span className="text-primary font-medium flex items-center gap-1">
                  Prep now <ArrowRight size={16} />
                </span>
              </button>
            )}

            {/* Compare offers */}
            {stats.offers >= 2 && (
              <button
                onClick={() => onNavigate('offers')}
                className="w-full flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-200 hover:border-green-300 hover:shadow-sm transition-all cursor-pointer text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-slate-700">
                    Compare your {stats.offers} offers
                  </span>
                </div>
                <span className="text-green-700 font-medium flex items-center gap-1">
                  Compare <ArrowRight size={16} />
                </span>
              </button>
            )}
          </div>

          {/* Upcoming interviews */}
          {stats.interviews > 0 && (
            <UpcomingInterviews
              interviewData={interviewData}
              loading={loadingInterviews}
              onNavigate={onNavigate}
            />
          )}

          {/* Recent applications */}
          {recentApplications.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-medium text-slate-900">Recent</h2>
                <button
                  onClick={() => onNavigate('library')}
                  className="text-sm text-primary hover:text-primary/80 transition-colors cursor-pointer"
                >
                  View all
                </button>
              </div>

              <div className="space-y-1">
                {recentApplications.map((app) => (
                  <button
                    key={app.id}
                    onClick={() => onViewApplication ? onViewApplication(app) : onNavigate('library')}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer text-left"
                  >
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusColors[app.status]}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {app.company || 'Unknown Company'}
                      </p>
                      <p className="text-sm text-slate-500 truncate">
                        {app.role || 'Role not specified'}
                      </p>
                    </div>
                    <span className="text-sm text-slate-400 flex-shrink-0">
                      {formatDate(app.timestamp)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

        </div>
      )}

    </div>
  );
}

export default DashboardView;
